// v2/pages/admin/AdminOverviewV2.js — Yonetim paneli / Genel Bakis (/v2/admin)
// Veri: /api/v2/admin/overview/ (tutarlar KDV haric, yalnizca Logo'ya aktarilan siparisler ciroya girer)
import React, { useMemo } from 'react';
import {
  ResponsiveContainer, ComposedChart, Bar, Line, XAxis, YAxis, Tooltip, CartesianGrid,
} from 'recharts';
import {
  RotateCw, TrendingUp, TrendingDown, Wallet, CalendarDays, Receipt, ShoppingCart,
  LogIn, Users, AlertTriangle, Clock, ShieldAlert, Inbox, Trophy, BarChart3, ShoppingBag,
} from 'lucide-react';
import useAdminOverview from '../../hooks/useAdminOverview';
import useMedia from '../../hooks/useMedia';
import { formatTRY, formatTRYShort, formatNumber, formatCompact } from '../../utils/format';
import {
  zamanTR, gunKisa, gunUzun, basHarfler, DEPO_ETIKET, SIPARIS_DURUM,
} from '../../utils/adminFormat';
import { Skel, SkelPanel } from '../../components/Skeleton';
import s from '../../styles/AdminOverviewV2.module.css';
import '../../styles/tokens.css';

const RENK_CIRO = '#2563C7';
const RENK_SIPARIS = '#16A36A';

function Degisim({ v }) {
  const n = Number(v);
  if (v === null || v === undefined || !Number.isFinite(n)) return null;
  const artis = n >= 0;
  const Ikon = artis ? TrendingUp : TrendingDown;
  return (
    <span className={`${s.delta} ${artis ? s.up : s.down}`} title="Geçen ayın aynı dönemine göre">
      <Ikon size={12} strokeWidth={2.5} />
      {artis ? '+' : '−'}%{formatNumber(Math.abs(n), 1)}
    </span>
  );
}

function Kpi({ ikon: Ikon, ton, etiket, deger, alt, degisim, yukleniyor, ipucu }) {
  return (
    <div className={s.kpi} title={ipucu || ''}>
      <div className={s.kpiTop}>
        <span className={`${s.kpiIcon} ${s[ton]}`}><Ikon size={18} /></span>
        <span className={s.kpiLabel}>{etiket}</span>
        {!yukleniyor && <Degisim v={degisim} />}
      </div>
      <div className={s.kpiValue}>{yukleniyor ? <Skel w="68%" h={22} /> : deger}</div>
      <div className={s.kpiSub}>{yukleniyor ? <Skel w="52%" h={10} /> : alt}</div>
    </div>
  );
}

function Durum({ st }) {
  const d = SIPARIS_DURUM[st] || SIPARIS_DURUM.processing;
  return <span className={`${s.status} ${s[d.cls]}`}>{d.label}</span>;
}

function Bos({ ikon: Ikon = Inbox, metin }) {
  return (
    <div className={s.empty}>
      <Ikon size={22} />
      <span>{metin}</span>
    </div>
  );
}

function GrafikIpucu({ active, payload, label }) {
  if (!active || !payload || !payload.length) return null;
  const p = payload[0].payload || {};
  return (
    <div className={s.tip}>
      <div className={s.tipTitle}>{gunUzun(label)}</div>
      <div className={s.tipRow}>
        <span><i className={s.dot} style={{ background: RENK_CIRO }} />Ciro</span>
        <b>{formatTRY(p.revenue)}</b>
      </div>
      <div className={s.tipRow}>
        <span><i className={s.dot} style={{ background: RENK_SIPARIS }} />Sipariş</span>
        <b>{formatNumber(p.orders)}</b>
      </div>
    </div>
  );
}

export default function AdminOverviewV2() {
  const { data, loading, error, yenile } = useAdminOverview();
  const mobil = useMedia('(max-width: 640px)');
  const ilk = loading && !data;

  const k = (data && data.kpis) || {};
  const sepet = (data && data.cart) || {};
  const kuyruk = (data && data.queue) || {};
  const giris = (data && data.logins) || {};
  const kul = (data && data.users) || {};
  const kis = (data && data.restrictions) || {};
  const seri = useMemo(() => (data && data.series) || [], [data]);
  const enler = (data && data.top_customers) || [];
  const sonSip = (data && data.recent_orders) || [];
  const sonGiris = giris.recent || [];

  const seriToplam = useMemo(
    () => seri.reduce(
      (a, g) => ({ ciro: a.ciro + (Number(g.revenue) || 0), sip: a.sip + (Number(g.orders) || 0) }),
      { ciro: 0, sip: 0 },
    ),
    [seri],
  );
  const enYuksek = enler.length ? Number(enler[0].revenue) || 0 : 0;
  const ortSepet = k.orders_month ? (Number(k.revenue_month) || 0) / k.orders_month : null;

  const uyarilar = [];
  if (kuyruk.errors > 0) {
    uyarilar.push({
      id: 'err', ton: s.alertRed, ikon: AlertTriangle,
      baslik: `${formatNumber(kuyruk.errors)} sipariş Logo aktarımında hata aldı`,
      metin: 'Bu kayıtlar Logo\'ya ulaşmamış olabilir. Tarihlerini kontrol edin.',
      ogeler: kuyruk.error_items || [],
    });
  }
  if (kuyruk.stuck > 0) {
    uyarilar.push({
      id: 'stuck', ton: s.alertAmber, ikon: Clock,
      baslik: `${formatNumber(kuyruk.stuck)} sipariş ${kuyruk.stuck_minutes || 15} dakikadan uzun süredir aktarım bekliyor`,
      metin: 'Senkronizasyon servisinin çalıştığını kontrol edin.',
    });
  }
  if (kis.active > 0) {
    uyarilar.push({
      id: 'kis', ton: s.alertAmber, ikon: ShieldAlert,
      baslik: `${formatNumber(kis.active)} müşteride borç kısıtlaması aktif`,
      metin: `Toplam ${formatNumber(kis.total)} kısıtlama kaydı var.`,
    });
  }

  if (error && !data) {
    return (
      <div className={s.page}>
        <div className={s.errorBox}>
          <AlertTriangle size={26} />
          <p className={s.errorTitle}>Genel bakış verileri alınamadı</p>
          <p className={s.errorText}>{error}</p>
          <button type="button" className={s.refresh} onClick={() => yenile(true)}>
            <RotateCw size={15} /> <span className={s.refreshText}>Tekrar dene</span>
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={s.page}>
      <div className={s.head}>
        <div className={s.headText}>
          <h2 className={s.title}>Genel Bakış</h2>
          <p className={s.subtitle}>
            {data
              ? <>Son güncelleme <b>{zamanTR(data.generated_at)}</b> · 2 dakikada bir yenilenir · tutarlar KDV hariç</>
              : 'Veriler yükleniyor…'}
          </p>
        </div>
        <button
          type="button"
          className={s.refresh}
          onClick={() => yenile(true)}
          disabled={loading}
          aria-label="Yenile"
        >
          <RotateCw size={15} className={loading ? s.spin : ''} />
          <span className={s.refreshText}>{loading ? 'Yenileniyor' : 'Yenile'}</span>
        </button>
      </div>

      {error && data && (
        <div className={s.stale}>Veriler güncellenemedi ({error}). Ekrandaki bilgiler son başarılı yüklemeye ait.</div>
      )}

      {uyarilar.length > 0 && (
        <div className={s.alerts}>
          {uyarilar.map((u) => {
            const Ikon = u.ikon;
            return (
              <div key={u.id} className={`${s.alert} ${u.ton}`} role="status">
                <Ikon size={20} className={s.alertIcon} />
                <div className={s.alertBody}>
                  <p className={s.alertTitle}>{u.baslik}</p>
                  <p className={s.alertText}>{u.metin}</p>
                  {u.ogeler && u.ogeler.length > 0 && (
                    <div className={s.chips}>
                      {u.ogeler.map((o) => (
                        <span key={o.order_no} className={s.chip} title={o.customer_name || ''}>
                          <b>#{o.order_no}</b>
                          <span>{o.customer_code || '-'}</span>
                          <span>{zamanTR(o.time)}</span>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <div className={s.kpis}>
        <Kpi
          ikon={Wallet} ton="tBlue" etiket="Bugünkü Ciro" yukleniyor={ilk}
          deger={formatTRYShort(k.revenue_today)}
          alt={<><b>{formatNumber(k.orders_today)}</b> sipariş</>}
        />
        <Kpi
          ikon={CalendarDays} ton="tGreen" etiket="Bu Ay Ciro" yukleniyor={ilk}
          deger={formatTRYShort(k.revenue_month)}
          degisim={k.revenue_change}
          alt={<>Geçen ay aynı dönem <b>{formatTRYShort(k.revenue_prev_period)}</b></>}
        />
        <Kpi
          ikon={Receipt} ton="tViolet" etiket="Bu Ay Sipariş" yukleniyor={ilk}
          deger={formatNumber(k.orders_month)}
          degisim={k.orders_change}
          alt={<>Ortalama <b>{ortSepet === null ? '-' : formatTRYShort(ortSepet)}</b></>}
        />
        <Kpi
          ikon={ShoppingCart} ton="tAmber" etiket="Sepette Bekleyen" yukleniyor={ilk}
          ipucu="Müşterilerin sepetinde duran, henüz siparişe dönüşmemiş ürünler (KDV hariç)"
          deger={formatTRYShort(sepet.amount)}
          alt={<><b>{formatNumber(sepet.customers)}</b> müşteri · <b>{formatNumber(sepet.lines)}</b> kalem</>}
        />
        <Kpi
          ikon={LogIn} ton="tSky" etiket="Bugünkü Girişler" yukleniyor={ilk}
          deger={formatNumber(giris.today)}
          alt={<><b>{formatNumber(giris.users_today)}</b> kullanıcı · <b>{formatNumber(giris.customers_today)}</b> müşteri</>}
        />
        <Kpi
          ikon={Users} ton="tSlate" etiket="Müşteri Kullanıcıları" yukleniyor={ilk}
          deger={formatNumber(kul.customer_active)}
          alt={<><b>{formatNumber(kul.customers_with_users)}</b> cari · {formatNumber(kul.customer_passive)} pasif · {formatNumber(kul.admins)} yönetici</>}
        />
      </div>

      <section className={s.panel}>
        <div className={s.panelHead}>
          <h3 className={s.panelTitle}><BarChart3 size={17} /> Son 30 Gün</h3>
          <div className={s.panelMeta}>
            <span><i className={s.dot} style={{ background: RENK_CIRO }} />Ciro <b>{formatTRYShort(seriToplam.ciro)}</b></span>
            <span><i className={s.dot} style={{ background: RENK_SIPARIS }} />Sipariş <b>{formatNumber(seriToplam.sip)}</b></span>
          </div>
        </div>
        <div className={s.panelBody}>
          {ilk ? (
            <Skel w="100%" h={mobil ? 220 : 280} shape="box" />
          ) : seri.length === 0 ? (
            <Bos metin="Bu dönemde sipariş yok" />
          ) : (
            <div className={s.chartWrap}>
              <ResponsiveContainer width="100%" height={mobil ? 220 : 280}>
                <ComposedChart data={seri} margin={{ top: 8, right: mobil ? 0 : 4, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#EEF2F7" />
                  <XAxis
                    dataKey="date" tickFormatter={gunKisa}
                    tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false}
                    interval={mobil ? 6 : 2} minTickGap={8}
                  />
                  <YAxis
                    yAxisId="ciro" tickFormatter={formatCompact}
                    tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false}
                    width={mobil ? 36 : 46}
                  />
                  <YAxis
                    yAxisId="sip" orientation="right" allowDecimals={false}
                    tick={{ fontSize: 11, fill: '#64748B' }} axisLine={false} tickLine={false}
                    width={mobil ? 0 : 30} hide={mobil}
                  />
                  <Tooltip content={<GrafikIpucu />} cursor={{ fill: 'rgba(37,99,199,.06)' }} />
                  <Bar yAxisId="ciro" dataKey="revenue" name="Ciro" fill={RENK_CIRO} radius={[4, 4, 0, 0]} maxBarSize={22} />
                  <Line
                    yAxisId="sip" dataKey="orders" name="Sipariş" type="monotone"
                    stroke={RENK_SIPARIS} strokeWidth={2} dot={false} activeDot={{ r: 4 }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      </section>

      <div className={s.grid2}>
        <section className={s.panel}>
          <div className={s.panelHead}>
            <h3 className={s.panelTitle}><ShoppingBag size={17} /> Son Siparişler</h3>
          </div>
          <div className={s.panelBody}>
            {ilk ? (
              <SkelPanel rows={4} head={false} />
            ) : sonSip.length === 0 ? (
              <Bos metin="Henüz sipariş yok" />
            ) : (
              <>
                <div className={s.tableWrap}>
                  <table className={s.table}>
                    <thead>
                      <tr>
                        <th className={s.th}>Müşteri</th>
                        <th className={s.th}>Zaman / Depo</th>
                        <th className={`${s.th} ${s.num}`}>Kalem / Adet</th>
                        <th className={`${s.th} ${s.num}`}>Tutar</th>
                        <th className={s.th}>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sonSip.map((o) => (
                        <tr key={`${o.code}-${o.time}`}>
                          <td className={s.td}>
                            <span className={s.cust}>
                              <span className={s.custName} title={o.name || ''}>{o.name || o.code}</span>
                              <span className={s.custCode}>{o.code}</span>
                            </span>
                          </td>
                          <td className={s.td}>
                            <span className={s.cust}>
                              <span className={s.timeMain}>{zamanTR(o.time)}</span>
                              <span className={s.custCode}>{DEPO_ETIKET[o.warehouse] || o.warehouse}</span>
                            </span>
                          </td>
                          <td className={`${s.td} ${s.num}`}>{formatNumber(o.lines)} / {formatNumber(o.qty)}</td>
                          <td className={`${s.td} ${s.num} ${s.strong}`}>{formatTRY(o.amount)}</td>
                          <td className={s.td}><Durum st={o.state} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <ul className={s.mList}>
                  {sonSip.map((o) => (
                    <li key={`m-${o.code}-${o.time}`} className={s.mItem}>
                      <div className={s.mRow}>
                        <span className={s.cust}>
                          <span className={s.custName}>{o.name || o.code}</span>
                          <span className={s.custCode}>{o.code} · {DEPO_ETIKET[o.warehouse] || o.warehouse}</span>
                        </span>
                        <Durum st={o.state} />
                      </div>
                      <div className={s.mRow}>
                        <span className={s.muted}>{zamanTR(o.time)} · {formatNumber(o.lines)} kalem / {formatNumber(o.qty)} adet</span>
                        <span className={s.mAmount}>{formatTRY(o.amount)}</span>
                      </div>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </section>

        <section className={s.panel}>
          <div className={s.panelHead}>
            <h3 className={s.panelTitle}><Trophy size={17} /> Bu Ay En Çok Alım</h3>
          </div>
          <div className={s.panelBody}>
            {ilk ? (
              <SkelPanel rows={5} head={false} />
            ) : enler.length === 0 ? (
              <Bos metin="Bu ay henüz alım yok" />
            ) : (
              <ol className={s.rank}>
                {enler.map((m, i) => {
                  const oran = enYuksek > 0 ? Math.max(4, Math.round((Number(m.revenue) / enYuksek) * 100)) : 0;
                  return (
                    <li key={m.code} className={s.rankItem}>
                      <span className={`${s.rankNo} ${i === 0 ? s.rankFirst : ''}`}>{i + 1}</span>
                      <span className={s.cust}>
                        <span className={s.custName} title={m.name || ''}>{m.name || m.code}</span>
                        <span className={s.custCode}>{m.code}</span>
                      </span>
                      <span className={s.rankVal}>
                        <span className={s.rankAmount}>{formatTRYShort(m.revenue)}</span>
                        <span className={s.rankOrders}>{formatNumber(m.orders)} sipariş</span>
                      </span>
                      <span className={s.bar}><span className={s.barFill} style={{ width: `${oran}%` }} /></span>
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        </section>
      </div>

      <section className={s.panel}>
        <div className={s.panelHead}>
          <h3 className={s.panelTitle}><LogIn size={17} /> Son Girişler</h3>
          <div className={s.panelMeta}>
            <span>Bugün <b>{formatNumber(giris.today)}</b> giriş</span>
          </div>
        </div>
        <div className={s.panelBody}>
          {ilk ? (
            <SkelPanel rows={3} head={false} />
          ) : sonGiris.length === 0 ? (
            <Bos metin="Giriş kaydı yok" />
          ) : (
            <ul className={s.logins}>
              {sonGiris.map((g, i) => (
                <li key={`${g.customer_code}-${g.time}-${i}`} className={s.loginItem}>
                  <span className={s.loginAv}>{basHarfler(g.name)}</span>
                  <span className={s.loginBody}>
                    <span className={s.loginName}>{(g.name || '').trim() || '-'}</span>
                    <span className={s.loginCust}>
                      {g.customer_code || '-'}{g.customer_name ? ` · ${g.customer_name}` : ''}
                    </span>
                  </span>
                  <span className={s.loginTime}>{zamanTR(g.time)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>
    </div>
  );
}
